import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Language, WINDOW_TOKEN } from '@raptor-fe/core';
import { StorageKeys, StorageService, TranslationService } from '@raptor-fe/services';
import { pipe, switchMap, tap } from 'rxjs';

interface LanguageModelState {
    languageList: Language[];
    currentLanguage: Language;
    newLanguage: Language;
    isLoading: boolean;
    filterTerm: string;
    errorMessage: string | null;
}

export const LanguageModalStore = signalStore(
    withState<LanguageModelState>({
        languageList: [],
        currentLanguage: { id: 'en-US', name: 'English (US)' },
        newLanguage: { id: 'en-US', name: 'English (US)' },
        isLoading: false,
        filterTerm: '',
        errorMessage: null,
    }),
    withMethods((store, service = inject(TranslationService), browserWindow = inject(WINDOW_TOKEN)) => ({
        // fetch available languages
        fetchAvailableLanguages: rxMethod<void>(
            pipe(
                tap(() => patchState(store, { isLoading: true })),
                switchMap(() =>
                    service.getAvailableLanguages().pipe(
                        tapResponse({
                            next: (languages) => {
                                patchState(store, { languageList: languages });
                            },
                            error: () => patchState(store, { errorMessage: 'Error fetching available languages' }),
                            finalize: () => patchState(store, { isLoading: false }),
                        }),
                    ),
                ),
            ),
        ),

        // get translations for selected language and set as current language
        getTranslations: rxMethod<Language>(
            pipe(
                tap(() => patchState(store, { isLoading: true })),
                switchMap((language) =>
                    service.getDictionary(language).pipe(
                        tapResponse({
                            next: () => {
                                patchState(store, { currentLanguage: language });
                                browserWindow.location.reload();
                            },
                            error: () => patchState(store, { errorMessage: 'Error fetching translation dictionary' }),
                            finalize: () => patchState(store, { isLoading: false }),
                        }),
                    ),
                ),
            ),
        ),

        // update search term for filtering languages in the modal
        updateSearchTerm: (term: string) => {
            patchState(store, { filterTerm: term });
        },

        // update current language selection in the modal
        updateNewLanguage: (languageId: string) => {
            const language = store.languageList().find((lang) => lang.id === languageId);
            if (language) {
                patchState(store, { newLanguage: language });
            }
        },
    })),
    withHooks((store, storageService = inject(StorageService)) => ({
        async onInit() {
            const savedLanguage = await storageService.getObject<Language>(StorageKeys.LANGUAGE_SELECTED_KEY);
            if (savedLanguage) {
                patchState(store, { currentLanguage: savedLanguage, newLanguage: savedLanguage });
            }

            store.fetchAvailableLanguages();
        },
    })),
);
